import { useState, type FormEvent, type ReactNode } from "react";
import { AlertCircle, Check, ChevronRight, Copy, CreditCard, ExternalLink, Globe, History, Info } from "lucide-react";
import type { PlanPrice, Workspace } from "../api/contract";
import type { PublicationController, PublicationState } from "../hooks/usePublication";
import type { PublishingController } from "../hooks/usePublishing";
import { servingProblem, type ServingController } from "../hooks/useServingChange";
import { describeAction } from "../lib/actionCopy";
import type { ChangeRow } from "../lib/changes";
import { publishedAgo } from "../lib/publishControl";
import { displayOrigin, isReviewStale, liveStatus } from "../lib/workspaceState";
import { Spinner } from "./Spinner";

const ADDRESS_PROBLEMS = {
  invalid: "Use 4 to 63 lowercase letters, numbers or single hyphens.",
  reserved: "That address is kept for Stack.je. Try another.",
  taken: "That address is taken. Try another.",
  live: "Your website is already live at its address, so it can’t change here.",
  failed: "That didn’t save. Try again.",
} as const;

export function formatPrice(price: PlanPrice): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: price.currency.toUpperCase() }).format(price.amount / 100);
}

/** A web address with the site's own name first and the domain after it, breaking only between them. */
export function HostName({ address }: { address: string }) {
  const dot = address.indexOf(".");
  if (dot <= 0) return <span className="bw-host">{address}</span>;
  return (
    <span className="bw-host">
      {address.slice(0, dot)}<wbr /><span className="bw-host-base">{address.slice(dot)}</span>
    </span>
  );
}

/** The web address, and choosing it before the site first goes live. */
export function Address({ publishing, current, onChanged }: { publishing: PublishingController; current: string; onChanged: () => void }) {
  const status = publishing.status;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(status?.subdomain ?? "");
  if (!status || !editing) {
    return (
      <div className="bw-address">
        <span className="bw-slip-address"><HostName address={current} /></span>
        {status?.addressChangeable && (
          <button type="button" className="bw-btn bw-btn--text" onClick={() => { setValue(status.subdomain.startsWith("site-") ? "" : status.subdomain); setEditing(true); }}>
            Change
          </button>
        )}
      </div>
    );
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (await publishing.changeAddress(value.trim().toLowerCase())) {
      setEditing(false);
      onChanged();
    }
  };
  return (
    <form className="bw-address-form" aria-busy={publishing.savingAddress} onSubmit={(event) => void submit(event)}>
      <label htmlFor="bw-address">Your web address</label>
      <div className="bw-address-input">
        <input
          id="bw-address"
          value={value}
          autoFocus
          inputMode="url"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="yourbusiness"
          aria-invalid={publishing.addressProblem ? true : undefined}
          aria-describedby="bw-address-note"
          onChange={(event) => {
            setValue(event.target.value);
            publishing.clearAddressProblem();
          }}
        />
        <span>.{status.baseDomain}</span>
      </div>
      <p id="bw-address-note" className="bw-item-note" role={publishing.addressProblem ? "alert" : undefined}>
        {publishing.addressProblem ? ADDRESS_PROBLEMS[publishing.addressProblem] : "You can change it until your website goes live."}
      </p>
      <div className="bw-actions">
        <button type="submit" className="bw-btn bw-btn--primary" disabled={!value.trim() || publishing.savingAddress}>{publishing.savingAddress && <Spinner />}{publishing.savingAddress ? "Saving…" : "Use this address"}</button>
        <button type="button" className="bw-btn bw-btn--quiet" disabled={publishing.savingAddress} onClick={() => { setEditing(false); publishing.clearAddressProblem(); }}>Cancel</button>
      </div>
    </form>
  );
}

function useCopy(value: string) {
  const [copied, setCopied] = useState(false);
  return { copied, copy: () => void navigator.clipboard?.writeText(value).then(() => setCopied(true), () => setCopied(false)) };
}

/** The live address, big, with the two things people do with it. */
function LiveCard({ origin }: { origin: string }) {
  const { copied, copy } = useCopy(origin);
  return (
    <div className="bw-live-card">
      <span className="bw-live-card-address"><HostName address={displayOrigin(origin)} /></span>
      <div className="bw-live-card-actions">
        <a className="bw-btn bw-btn--primary" href={origin} target="_blank" rel="noopener noreferrer">
          Visit your site <ExternalLink size={16} aria-hidden="true" /><span className="bw-sr"> (opens in a new tab)</span>
        </a>
        <button type="button" className="bw-btn bw-btn--quiet" onClick={copy}>
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />} {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

/** Where the site is live now, kept small above the changes about to replace it. */
function LiveAt({ origin }: { origin: string }) {
  const { copied, copy } = useCopy(origin);
  return (
    <div className="bw-live-at">
      <span className="bw-live-at-text"><small>Live at</small><a href={origin} target="_blank" rel="noopener noreferrer">{displayOrigin(origin)}</a></span>
      <a className="bw-btn bw-btn--quiet bw-btn--icon" href={origin} target="_blank" rel="noopener noreferrer" aria-label="Visit your site (opens in a new tab)"><ExternalLink size={16} aria-hidden="true" /></a>
      <button type="button" className="bw-btn bw-btn--quiet bw-btn--icon" onClick={copy} aria-label={copied ? "Link copied" : "Copy link"}>
        {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
      </button>
    </div>
  );
}

function QuietRow({ icon, title, note, onClick }: { icon: ReactNode; title: string; note: string; onClick: () => void }) {
  return (
    <button type="button" className="bw-quiet-row" onClick={onClick}>
      {icon}
      <span><strong>{title}</strong><small>{note}</small></span>
      <ChevronRight size={16} aria-hidden="true" />
    </button>
  );
}

/** The quieter things to do once the site is live. */
function LiveExtras({ publishing, onOpenDomains, onOpenHistory }: { publishing: PublishingController; onOpenDomains?: () => void; onOpenHistory: () => void }) {
  const domains = onOpenDomains && publishing.status?.domains && !publishing.status.domain;
  return (
    <div className="bw-quiet-rows">
      {domains && <QuietRow icon={<Globe size={18} aria-hidden="true" />} title="Use your own domain" note="Like yourbusiness.sg. This address keeps working." onClick={onOpenDomains} />}
      <QuietRow icon={<History size={18} aria-hidden="true" />} title="Earlier versions" note="Every publish is kept. Put an old one back from here." onClick={onOpenHistory} />
    </div>
  );
}

/** The quiet way out: visitors see a not-found page until the owner publishes again. */
function TakeOffline({ serving }: { serving: ServingController }) {
  const [confirming, setConfirming] = useState(false);
  const working = serving.state.phase === "working";
  return (
    <div className="bw-take-offline">
      {confirming ? (
        <div className="bw-discard-confirm" role="group" aria-label="Take your site offline">
          <span>Your site will show a not-found page until you publish again. Your draft and every earlier version stay saved.</span>
          <button type="button" className="bw-btn bw-btn--primary" disabled={working} onClick={() => void serving.run({ kind: "unpublish" }).then((done) => done && setConfirming(false))}>
            {working && <Spinner />}
            {working ? "Taking offline…" : "Take offline"}
          </button>
          <button type="button" className="bw-btn bw-btn--quiet" disabled={working} onClick={() => { setConfirming(false); serving.clear(); }}>Keep it live</button>
        </div>
      ) : (
        <button type="button" className="bw-btn bw-btn--quiet bw-btn--danger" onClick={() => setConfirming(true)}>Take site offline</button>
      )}
      {serving.state.phase === "failed" && <p className="bw-item-note" role="alert">{servingProblem(serving.state)}</p>}
    </div>
  );
}

function Callout({ tone, title, children }: { tone: "attention" | "danger" | "done" | "neutral"; title: string; children?: ReactNode }) {
  return (
    <div className="bw-callout" data-tone={tone} role={tone === "danger" || tone === "attention" ? "alert" : "status"}>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
    </div>
  );
}

/** What goes live the first time: the pages, and any spot still waiting to be filled. */
function WhatGoesLive({ workspace }: { workspace: Workspace }) {
  const { pages, slotCount } = workspace;
  return (
    <section className="bw-publish-section" aria-labelledby="bw-what-goes-live">
      <h4 id="bw-what-goes-live">What goes live</h4>
      <dl className="bw-publish-facts">
        <div><dt>Pages</dt><dd>{pages.map((page) => page.title).join(", ") || "None"}</dd></div>
        {Boolean(slotCount) && <div><dt>Not filled</dt><dd>{slotCount === 1 ? "1 spot. It stays hidden until you add it." : `${slotCount} spots. They stay hidden until you add them.`}</dd></div>}
      </dl>
    </section>
  );
}

/** What changes for visitors with this publish; undoing happens in Changes. */
function ChangeSummary({ rows, onManage }: { rows: ChangeRow[]; onManage: () => void }) {
  const going = rows.filter((row) => !row.undone);
  if (going.length === 0) return null;
  return (
    <section className="bw-publish-section bw-publish-changes" aria-labelledby="bw-publish-changes">
      <div className="bw-publish-section-head">
        <h4 id="bw-publish-changes">What changes for visitors</h4>
        <button type="button" className="bw-btn bw-btn--text" onClick={onManage}>Undo a change</button>
      </div>
      <ol className="bw-change-list">
        {going.map((row) => {
          const copy = describeAction(row.action);
          return (
            <li key={row.action.id} className="bw-change-row">
              <span className="bw-change-main">
                <span className="bw-change-number" aria-hidden="true">{row.number}</span>
                <span className="bw-change-text">
                  <strong>{row.action.subject?.label ? `${copy.title}: ${row.action.subject.label}` : copy.title}</strong>
                  <small>{row.action.by === "admin" ? "Stax" : "You"}</small>
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** The Website's Publish never includes posts: each publishes or updates from the Blog area. */
export const POSTS_NOTE = "Posts aren't part of this. Each post publishes or updates from Blog.";

function PostsNote() {
  return <p className="bw-publish-posts-note"><Info size={16} aria-hidden="true" /><span>{POSTS_NOTE}</span></p>;
}

/** Publishing needs a plan; the publish button goes to checkout, and paying never publishes by itself. */
function PlanNote({ publishing, checkoutReturn }: { publishing: PublishingController; checkoutReturn: PublishPanelProps["checkoutReturn"] }) {
  const prices = publishing.status?.prices;
  const monthly = prices?.monthly ? `${formatPrice(prices.monthly)} a month` : null;
  const annual = prices?.annual ? `${formatPrice(prices.annual)} a year` : null;
  return (
    <div className="bw-plan-note">
      <CreditCard size={18} aria-hidden="true" />
      <div>
        <p>Publishing is on the Pro plan{monthly ? `, ${monthly}` : ""}. Building and previews stay free. Checkout comes next, then you confirm.</p>
        {annual && (
          <button type="button" className="bw-btn bw-btn--text" onClick={() => void publishing.subscribe("annual")} disabled={publishing.checkout === "starting"}>
            Pay yearly instead ({annual})
          </button>
        )}
        {checkoutReturn === "cancelled" && <p className="bw-item-note">Checkout was cancelled. Nothing was charged.</p>}
        {publishing.checkout === "failed" && <p className="bw-item-note" role="alert">Checkout couldn’t start. Try again.</p>}
      </div>
    </div>
  );
}

function PublishingSteps({ running }: { running: boolean }) {
  const steps = [
    { label: running ? "Ready to build" : "Waiting to start", state: running ? "done" : "current" },
    { label: "Building your site", state: running ? "current" : "next" },
    { label: "Putting it online", state: "next" },
  ] as const;
  return (
    <ol className="bw-publish-steps">
      {steps.map((step) => (
        <li key={step.label} data-state={step.state} aria-current={step.state === "current" ? "step" : undefined}>
          <span aria-hidden="true">{step.state === "done" ? <Check size={14} /> : step.state === "current" ? <Spinner /> : null}</span>
          {step.label}
        </li>
      ))}
    </ol>
  );
}

function Primary({ label, onClick, disabled, busy, secondary }: { label: string; onClick: () => void; disabled?: boolean; busy?: boolean; secondary?: { label: string; onClick: () => void } }) {
  return (
    <div className="bw-publish-footer">
      <button type="button" className="bw-btn bw-btn--primary bw-btn--action" onClick={onClick} disabled={disabled || busy}>
        {busy && <Spinner />}{label}
      </button>
      {secondary && <button type="button" className="bw-btn bw-btn--text bw-publish-secondary" onClick={secondary.onClick}>{secondary.label}</button>}
    </div>
  );
}

interface PublishPanelProps {
  publication: PublicationController;
  publishing: PublishingController;
  workspace: Workspace;
  /** The last workspace read failed transiently. */
  disconnected: boolean;
  /** The owner just came back from checkout. */
  checkoutReturn: "success" | "cancelled" | null;
  /** The address changed, so there is a new draft to review. */
  onAddressChanged: () => void;
  /** Opens where the owner connects their own domain. */
  onOpenDomains?: () => void;
  /** Opens earlier versions. */
  onOpenHistory: () => void;
  /** Unpublished changes, newest first. */
  changes: ChangeRow[];
  /** Opens Changes, where each change can be undone. */
  onManageChanges: () => void;
  /** Goes back to Stax's chat and asks it something. */
  onAskStax: (text: string) => void;
  /** The site has a blog, so the review says posts aren't part of this publish. */
  hasBlog?: boolean;
}

export interface PublishPanel {
  title: string;
  subtitle?: string;
  /** A mark before the title: live, or didn't go live. */
  tone?: "done" | "danger";
  body: ReactNode;
  /** The one primary action, pinned under the body. */
  footer?: ReactNode;
}

function changesTitle(rows: ChangeRow[]): string {
  const count = rows.filter((row) => !row.undone).length;
  return count ? `Publish ${count} ${count === 1 ? "change" : "changes"}` : "Publish your changes";
}

/** The live panel: what the Live button opens when there's nothing new to publish. */
export function livePanel({ workspace, publishing, serving, onOpenDomains, onOpenHistory }: Pick<PublishPanelProps, "workspace" | "publishing" | "onOpenDomains" | "onOpenHistory"> & { serving: ServingController }): PublishPanel {
  const ago = publishedAgo(workspace);
  return {
    title: "Live and up to date",
    subtitle: `${ago ? `Last published ${ago}. ` : ""}Edits you make show here as changes to publish.`,
    tone: "done",
    body: (
      <div className="bw-publish-body">
        <LiveCard origin={workspace.manifest.canonicalOrigin} />
        <LiveExtras publishing={publishing} onOpenDomains={onOpenDomains} onOpenHistory={onOpenHistory} />
        <TakeOffline serving={serving} />
      </div>
    ),
  };
}

/** One panel for every step of publishing: a title, what matters now, and one primary action. */
export function publishPanel(props: PublishPanelProps): PublishPanel | null {
  const { publication, publishing, workspace, disconnected, checkoutReturn, changes } = props;
  const state: PublicationState = publication.state;
  const address = displayOrigin(workspace.manifest.canonicalOrigin);
  const origin = workspace.manifest.canonicalOrigin;
  const firstTime = liveStatus(workspace).kind === "never-published";
  const heading = firstTime
    ? { title: "Put your website online", subtitle: "Pick its address, then publish. You can keep editing afterwards." }
    : { title: changesTitle(changes), subtitle: "Your live site stays as it is until you publish." };
  const reviewAgain = <Primary label="Review the latest version" onClick={publication.reviewAgain} disabled={disconnected} />;

  switch (state.phase) {
    case "idle":
      return null;

    case "preparing":
      return {
        ...heading,
        body: <div className="bw-publish-body"><div className="bw-progress" role="status"><span className="bw-dot" data-tone="progress" aria-hidden="true" />Getting your pages ready…</div></div>,
        footer: <Primary label={firstTime ? `Publish ${address}` : changesTitle(changes)} onClick={() => undefined} busy />,
      };

    case "prepare-unknown":
      return {
        title: "Stack.je couldn’t be reached",
        subtitle: "Your pages may not be ready yet. Trying again is safe and won’t publish anything.",
        body: null,
        footer: <Primary label="Try again" onClick={publication.retry} />,
      };

    case "reviewing":
    case "confirming": {
      const stale = state.action.review ? isReviewStale(state.action.review, workspace.site) : false;
      if (stale) {
        return {
          title: "Your website changed",
          subtitle: "You’re looking at an older version. Check the latest one before it goes live.",
          body: null,
          footer: reviewAgain,
        };
      }
      const confirming = state.phase === "confirming";
      const plan = publishing.status;
      const needsPlan = plan !== undefined && !plan.entitled;
      const justSubscribed = checkoutReturn === "success" && plan?.entitled === true;
      const label = firstTime ? `Publish ${address}` : changesTitle(changes);
      let footer: ReactNode;
      if (needsPlan && checkoutReturn === "success") {
        footer = publishing.waitedOut ? <Primary label="Check again" onClick={publishing.retry} /> : undefined;
      } else if (needsPlan) {
        footer = <Primary label={publishing.checkout === "starting" ? "Opening checkout…" : label} onClick={() => void publishing.subscribe("monthly")} busy={publishing.checkout === "starting"} disabled={disconnected} />;
      } else {
        footer = <Primary label={confirming ? "Publishing…" : label} onClick={publication.confirm} busy={confirming} disabled={disconnected || plan === undefined} />;
      }
      return {
        ...heading,
        body: (
          <div className="bw-publish-body">
            {justSubscribed && <Callout tone="done" title="Your plan is active">Nothing is published yet. Publish when you’re ready.</Callout>}
            {needsPlan && checkoutReturn === "success" && (publishing.waitedOut
              ? <Callout tone="attention" title="Your plan isn’t confirmed yet">If you finished paying, it can take a minute to come through. Nothing is published until you confirm.</Callout>
              : <div className="bw-progress" role="status"><span className="bw-dot" data-tone="progress" aria-hidden="true" />Confirming your plan…</div>)}
            {firstTime
              ? <Address publishing={publishing} current={address} onChanged={props.onAddressChanged} />
              : <LiveAt origin={origin} />}
            {firstTime ? <WhatGoesLive workspace={workspace} /> : !confirming && <ChangeSummary rows={changes} onManage={props.onManageChanges} />}
            {props.hasBlog && !confirming && <PostsNote />}
            {needsPlan && checkoutReturn !== "success" && <PlanNote publishing={publishing} checkoutReturn={checkoutReturn} />}
            {disconnected && <p className="bw-status" role="status">Reconnecting to Stack.je before you can publish.</p>}
          </div>
        ),
        footer,
      };
    }

    case "confirm-unknown":
      return {
        title: "Checking whether it went live",
        subtitle: "Stack.je didn’t answer. Checking again is safe: it won’t publish twice.",
        body: null,
        footer: <Primary label="Check again" onClick={publication.retry} />,
      };

    case "publishing":
      return {
        title: `Publishing ${address}`,
        subtitle: "Usually under a minute. It keeps going if you leave this.",
        body: (
          <div className="bw-publish-body" role="status">
            <PublishingSteps running={state.action.state === "running"} />
            {state.connection === "reconnecting" && <Callout tone="neutral" title="Reconnecting">You’ll see the result as soon as Stack.je answers.</Callout>}
            {state.stopRefused && <Callout tone="neutral" title="It’s already building">Publishing can’t be stopped at this point.</Callout>}
            {state.action.state === "queued" && !state.stopRefused && (
              <button type="button" className="bw-btn bw-btn--text" onClick={() => void publication.cancel()}>Stop publishing</button>
            )}
          </div>
        ),
      };

    case "settled": {
      const { action } = state;
      if (action.state === "succeeded") {
        return {
          title: "Your website is live",
          subtitle: "Published just now. Anyone with the link can visit it.",
          tone: "done",
          body: (
            <div className="bw-publish-body">
              <LiveCard origin={origin} />
              <LiveExtras publishing={publishing} onOpenDomains={props.onOpenDomains} onOpenHistory={props.onOpenHistory} />
            </div>
          ),
        };
      }
      if (action.state === "failed") {
        const pending = changes.filter((row) => !row.undone).length;
        return {
          title: "That didn’t go live",
          subtitle: firstTime
            ? "Your website isn’t live yet, and everything you made is still saved."
            : `Your live site hasn’t changed, and ${pending ? `your ${pending === 1 ? "change is" : `${pending} changes are`}` : "your changes are"} still saved.`,
          tone: "danger",
          body: (
            <div className="bw-publish-body">
              <div className="bw-publish-explain">
                <strong>What happened</strong>
                <p>Publishing stopped partway. This is usually brief, and trying again is safe.</p>
              </div>
            </div>
          ),
          footer: <Primary label="Try again" onClick={publication.reviewAgain} disabled={disconnected} secondary={{ label: "Ask Stax about it", onClick: () => props.onAskStax("My last publish didn’t go live. What went wrong?") }} />,
        };
      }
      return action.state === "conflict"
        ? { title: "A newer publish replaced this one", subtitle: "This one didn’t change your live site.", body: null }
        : { title: "Publishing was stopped", subtitle: "Your live site didn’t change.", body: null };
    }

    case "blocked":
      if (state.reason === "stale" || state.reason === "changed") {
        return {
          title: "Your website changed",
          subtitle: "Nothing was published. Check the latest version before it goes live.",
          body: null,
          footer: reviewAgain,
        };
      }
      if (state.reason === "refused" && state.stage === "prepare") {
        return {
          title: "This version can’t go live yet",
          subtitle: "Nothing was published. Check that your website has a home page, then try again.",
          tone: "danger",
          body: null,
        };
      }
      if (state.reason === "refused") {
        return {
          title: "That couldn’t be published",
          subtitle: "Nothing was published.",
          tone: "danger",
          body: <div className="bw-publish-body"><p className="bw-publish-explain">Check it again, or <a className="bw-link" href="/subscription">check your plan</a>.</p></div>,
          footer: <Primary label="Check it again" onClick={publication.reviewAgain} disabled={disconnected} />,
        };
      }
      // Session and ownership failures replace the whole workspace; see BuilderWorkspace.
      return null;
  }
}

/** The mark before a publish panel's title. */
export function PublishMark({ tone }: { tone: "done" | "danger" }) {
  return (
    <span className="bw-publish-mark" data-tone={tone} aria-hidden="true">
      {tone === "done" ? <Check size={18} strokeWidth={2.5} /> : <AlertCircle size={18} />}
    </span>
  );
}
